/**
 * Formatting for the variant (size/colour) of a cart or order line.
 *
 * Commerce attribute *key* names are free-text and in practice arrive lowercase
 * ("colour"), while their *values* are cased by the editor ("Yellow"). Only the
 * key's first letter is normalised — the value is shown exactly as authored.
 */

import type { CartLineAttribute } from "@/types/cart";

/** Separator between attributes when a variant is defined by more than one. */
const ATTRIBUTE_SEPARATOR = " · ";

function upperFirst(value: string): string {
  if (!value) return value;
  return value.charAt(0).toLocaleUpperCase() + value.slice(1);
}

/** One attribute as "Colour: Yellow". */
export function formatVariantAttribute(attribute: CartLineAttribute): string {
  const key = upperFirst(attribute.keyName?.trim() ?? "");
  const value = attribute.valueName?.trim() ?? "";
  if (!key) return value;
  if (!value) return key;
  return `${key}: ${value}`;
}

/**
 * The whole variant as "Colour: Yellow · Size: M", or null when the line has no
 * variant (a product sold in a single version).
 */
export function formatVariantAttributes(
  attributes: readonly CartLineAttribute[] | null | undefined
): string | null {
  if (!attributes?.length) return null;
  const parts = attributes.map(formatVariantAttribute).filter(Boolean);
  return parts.length ? parts.join(ATTRIBUTE_SEPARATOR) : null;
}
